import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { forkJoin as RxForkJoin } from 'rxjs/observable/forkJoin';
import { of as RxOf } from 'rxjs/observable/of';
import { map, mergeMap, filter, debounceTime } from 'rxjs/operators';
import {
  isNil as _isNil,
  isUndefined as _isUndefined,
  filter as _filter,
  map as _map,
  split as _split,
  last as _last,
  omit as _omit,
  cloneDeep as _cloneDeep,
  assign as _assign,
  concat as _concat,
  compact as _compact,
  sortBy as _sortBy,
  forEach as _forEach,
  head as _head,
  find as _find
} from 'lodash';
import { DateTimeUtils, UrlUtils } from '@blcloud/bl-common';
import {
  ILoginUserEmployeeBindModel,
  IContact,
  ITaskInfo,
  IWorkingTaskInfo,
  IContactExecuteResult,
  IContactLostReason,
  ICustomerContactDisp,
  IContactHistory,
  IContactHistoryOrigin,
  ISlipKindKey,
  IVehicleInfo
} from '@blcloud/bl-datamodel';
import { CodeDivGenre } from '@blcloud/bl-datamodel/enum/common/code-div-genre';
import { ContactCreateDiv } from '@blcloud/bl-datamodel/enum/deals/contact-create-div';
import { WorkStatusDiv } from '@blcloud/bl-datamodel/enum/process/work-status-div';
import { ProductCode } from '@blcloud/bl-datamodel/enum/common/product-code';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { ContactExecuteResultDiv } from '@blcloud/bl-datamodel/enum/deals/contact-execute-result-div';
import {
  ContactResource,
  ContactHistoryResource,
  TaskInfoResource,
  CustomerContactDispResource,
  ContactHistoryOriginResource,
  VehicleInfoResource
} from '@blcloud/bl-ng-resource';
import { BlApiQueryParams, BlConditionInfo, BlConditionType, BlUrlQuery, BlUrlQueryKey } from '@blcloud/bl-ng-common';
import { Rules } from '@blcloud/bl-ng-ui-component';
import { LoginResourceService, DivisionResourceService, FrontDivisionBody } from '@blcloud/bl-ng-share-module';
import { TransitionDeciderService } from '@blcloud/bl-ng-vehicle-customer-module';
import { PagePath } from '../../../core/const/page-path.const';
import { ContactService } from '../../../shared/contact/contact.service';
import { IContactEx } from '../call-list-page.interface';
import { IScheduleHistory, IStatusCount, IRelatedContact, CONTACT_RESULT_CODE, DEFINE } from './result-form-modal.interface';

/**
 * 結果入力モーダルサービス
 */
@Injectable()
export class ResultFormModalService {

  /** コンタクト関連情報検索依頼通知 */
  private requestFetchRelatedContactSubject = new Subject<IContactEx>();

  /** コンタクト関連情報通知 */
  private relatedContactSubject = new BehaviorSubject<IRelatedContact>(undefined);

  /** コンタクト実施結果リスト */
  private contactExecuteResultList: IContactExecuteResult[];

  /**
   * コンストラクタ
   * @param loginService ログイン管理サービス
   * @param divisionService 区分マスタリソースサービス
   * @param contactService コンタクト系共通サービス
   * @param contactResource コンタクト情報リソース
   * @param contactHistoryResource コンタクト履歴リソース
   * @param taskInfoResource タスク情報リソース
   * @param customerContactDispResource 顧客接点表示情報リソース
   * @param contactHistoryOriginResource コンタクト履歴発生元リソース
   * @param vehicleInfoResource 車両情報リソース
   * @param transitionDeciderService 遷移先決定サービス
   */
  constructor(
    private loginService: LoginResourceService,
    private divisionService: DivisionResourceService,
    private contactService: ContactService,
    private contactResource: ContactResource,
    private contactHistoryResource: ContactHistoryResource,
    private taskInfoResource: TaskInfoResource,
    private customerContactDispResource: CustomerContactDispResource,
    private contactHistoryOriginResource: ContactHistoryOriginResource,
    private vehicleInfoResource: VehicleInfoResource,
    private transitionDeciderService: TransitionDeciderService
  ) {
    // コンタクト関連情報検索
    this.requestFetchRelatedContactSubject.pipe(
      debounceTime(500) // 連続送信は間引く
    ).subscribe(
      contact => {
        this.fatchRelatedContact(contact);
      }
    );
  }

  /**
   * 終了処理を行います。
   */
  destroy(): void {
    this.requestFetchRelatedContactSubject.complete();
    this.relatedContactSubject.complete();
  }

  /**
   * コンタクト関連情報
   */
  get relatedContact$(): Observable<IRelatedContact> {
    return this.relatedContactSubject.asObservable().pipe(
      filter(response => !_isUndefined(response))
    );
  }

  /**
   * 実施件数を取得します。
   * @param contactList 拡張コンタクト情報リスト
   * @returns 実施件数
   */
  getStatusCount(contactList: IContactEx[]): IStatusCount {
    const statusCount: IStatusCount = { notImplemented: 0, implemented: 0 };
    _forEach(contactList, contact => {
      if (!_isNil(contact.contactHistory)) {
        statusCount.implemented++;
      } else {
        statusCount.notImplemented++;
      }
    });
    return statusCount;
  }

  /**
   * 結果入力エンティティを生成します。
   * @param contact 拡張コンタクト情報
   * @returns 結果入力エンティティ
   */
  createEntity(contact: IContactEx): IWorkingTaskInfo {

    let entity: IWorkingTaskInfo;

    // タスク情報が存在する場合は「下書き」or「コンタクト予定」
    if (!_isNil(contact.taskInfo)) {
      if (contact.taskInfo.workStatusDiv === WorkStatusDiv.Working) {
        // 下書き
        entity = {
          reContactEmployeeCode: contact.taskInfo.workingTaskInfo.reContactEmployeeCode,
          reContactEmployeeName: contact.taskInfo.workingTaskInfo.reContactEmployeeName,
          reContactImplScheduleDate: contact.taskInfo.workingTaskInfo.reContactImplScheduleDate,
          contactComment: contact.taskInfo.workingTaskInfo.contactComment,
          contactExecuteResultCode: contact.taskInfo.workingTaskInfo.contactExecuteResultCode,
          contactExecuteResult: contact.taskInfo.workingTaskInfo.contactExecuteResult,
          contactLostReasonCode: contact.taskInfo.workingTaskInfo.contactLostReasonCode,
          contactLostReason: contact.taskInfo.workingTaskInfo.contactLostReason
        };
      } else {
        // コンタクト予定
        entity = {
          reContactEmployeeCode: contact.reContactEmployeeCode,
          reContactEmployeeName: contact.reContactEmployeeName,
          reContactImplScheduleDate: contact.reContactImplScheduleDate,
          contactComment: contact.taskInfo.contactComment
        };
      }

    } else if (!_isNil(contact.contactHistory)) {
      // コンタクト履歴が存在する場合は「コンタクト結果」
      entity = {
        reContactEmployeeCode: contact.reContactEmployeeCode,
        reContactEmployeeName: contact.reContactEmployeeName,
        reContactImplScheduleDate: contact.reContactImplScheduleDate,
        contactComment: contact.contactHistory.contactComment,
        contactExecuteResultCode: contact.contactHistory.contactExecuteResultCode,
        contactExecuteResult: contact.contactHistory.contactExecuteResult,
        contactLostReasonCode: contact.contactHistory.contactLostReasonCode,
        contactLostReason: contact.contactHistory.contactLostReason,
      };
    }

    return entity;
  }

  /**
   * 画面起動処理を行います。
   *
   * 画面起動に必要な以下のデータを生成して返します。
   * ・ログインユーザー
   * ・連絡先種別(電話)区分リスト
   * ・コンタクト実施結果リスト
   * ・コンタクト失注理由リスト
   * ・入力検証ルール
   * @param organizationCode 組織コード
   * @param isImplemented 実施済フラグ
   * @returns 起動データ
   */
  startUp(organizationCode: string, isImplemented: boolean)
    : Observable<[ILoginUserEmployeeBindModel, FrontDivisionBody[], IContactExecuteResult[], IContactLostReason[], Rules[]]> {
    return RxForkJoin(
      this.loginService.getLoginUserEmployeeBindModel(),
      this.divisionService.fetchTransformFrontDivision([CodeDivGenre.ContactTypeTel]),
      this.searchContactExecuteResultList(organizationCode),
      this.contactService.searchContactLostReasonList(organizationCode),
      this.getCustomRules(isImplemented)
    ).pipe(
      map(
        ([loginUser, division, contactExecuteResultList, contactLostReasonList, rules]) => {
          // 失注判定用に保持
          this.contactExecuteResultList = contactExecuteResultList;

          return [
            loginUser,
            division[CodeDivGenre.ContactTypeTel],
            contactExecuteResultList,
            contactLostReasonList,
            rules
          ] as [ILoginUserEmployeeBindModel, FrontDivisionBody[], IContactExecuteResult[], IContactLostReason[], Rules[]];
        }
      )
    );
  }

  /**
   * 対象コンタクト情報の関連データ検索を依頼します。
   * @param contact 拡張コンタクト情報
   */
  requestFetchRelatedContact(contact: IContactEx): void {
    // 検索依頼
    this.requestFetchRelatedContactSubject.next(_cloneDeep(contact));
  }

  /**
   * コンタクト情報の下書きを保存して返します。
   * @param contact 拡張コンタクト情報
   * @param entity 画面表示エンティティ
   * @returns 拡張コンタクト情報
   */
  saveDraftContact(contact: IContactEx, entity: IWorkingTaskInfo): Observable<IContactEx> {
    contact.taskInfo.workStatusDiv = WorkStatusDiv.Working;
    contact.taskInfo.workingTaskInfo = entity;

    // 下書き保存(実施はタスクを更新する)
    return this.taskInfoResource.putByEntity<ITaskInfo>(
      contact.taskInfo,
      { entityOptions: { exPath: 'updatetask' } }
    ).pipe(
      map(
        taskInfo => {
          contact.taskInfo = taskInfo;
          return contact;
        }
      )
    );
  }

  /**
   * コンタクト情報を更新して返します。
   * @param contact 拡張コンタクト情報
   * @param entity 画面表示エンティティ
   * @returns 拡張コンタクト情報
   */
  updateContact(contact: IContactEx, entity: IWorkingTaskInfo): Observable<IContactEx> {
    return this.loginService.getLoginUserEmployeeBindModel().pipe(
      mergeMap(
        loginUser => {
          if (!_isNil(contact.taskInfo)) {
            contact.taskInfo.workStatusDiv = WorkStatusDiv.NoneSchduled;
          }

          contact.reContactEmployeeCode = entity.reContactEmployeeCode;
          contact.reContactEmployeeName = entity.reContactEmployeeName;
          contact.reContactImplScheduleDate = entity.reContactImplScheduleDate;

          if (_isNil(contact.contactHistory)) {
            contact.contactHistory = {
              contactDateTime: DateTimeUtils.formatIso(DateTimeUtils.now())
            };
          }

          contact.contactHistory.contactExecuteResultCode = entity.contactExecuteResultCode;
          contact.contactHistory.contactExecuteResult = entity.contactExecuteResult;
          contact.contactHistory.contactEmployeeCode = loginUser.employeeCode;
          contact.contactHistory.contactEmployeeName = loginUser.employeeName;
          contact.contactHistory.contactComment = entity.contactComment;

          // 実施結果区分が失注なら理由を登録
          const executeResult = _find(this.contactExecuteResultList, contactExecuteResult => {
            return contactExecuteResult.contactExecuteResultCode === contact.contactHistory.contactExecuteResultCode;
          });
          if (!_isNil(executeResult) && executeResult.contactExecuteResultDiv === ContactExecuteResultDiv.Lostorder) {
            contact.contactHistory.contactLostReasonCode = entity.contactLostReasonCode;
            contact.contactHistory.contactLostReason = entity.contactLostReason;
          }

          // タスク情報からコンタクト履歴へ必要なパラメータを設定
          contact.contactHistory.contactExecuteResultDiv = executeResult.contactExecuteResultDiv;
          if (!_isNil(contact.taskInfo)) {
            contact.contactHistory.organizationCode = contact.taskInfo.organizationCode;
            contact.contactHistory.customerCode = contact.taskInfo.customerCode;
            contact.contactHistory.contactCheckDiv = contact.taskInfo.contactCheckDiv;
            contact.contactHistory.contactCheckDivName = contact.taskInfo.contactCheckDivName;
            contact.contactHistory.contactExecuteItemCode = contact.taskInfo.contactExecuteItemCode;
            contact.contactHistory.contactMethodCode = contact.taskInfo.contactMethodCode;
            contact.contactHistory.contactMethodName = contact.taskInfo.contactMethodName;
            if (!_isNil(contact.taskInfo.subjects.sf)) {
              contact.contactHistory.vehicleMgtNumber = contact.taskInfo.subjects.sf.vehicleMgtNumber;
            } else if (!_isNil(contact.taskInfo.subjects.repair)) {
              contact.contactHistory.vehicleMgtNumber = contact.taskInfo.subjects.repair.vehicleMgtNumber;
            }
          }

          const gridData = contact.gridData;
          return this.contactResource.putByEntity<IContact>(_omit(contact, 'gridData')).pipe(
            map(
              response => {
                if (!_isNil(response.taskInfo)
                  && (response.taskInfo.logicalDeleteDiv === LogicalDeleteDiv.Deleted
                    || response.taskInfo.executeDivCode === DEFINE.EXECUTE_DIV_IMPLEMENTED)) {
                  delete response.taskInfo;
                }
                return _assign(response, { gridData });
              }
            )
          );
        }
      )
    );
  }

  /**
   * コンタクト実施結果に対応する遷移先URLを取得します。
   * @param contactHistory コンタクト履歴
   * @returns 遷移先URL
   */
  getTransitionUrl(contactHistory: IContactHistory): Observable<string> {
    if (contactHistory.contactExecuteResultCode === CONTACT_RESULT_CODE.RECEPTION) {
      return RxOf(this.transitionDeciderService.getReceptionUrl(contactHistory.consumerExecTableKey));

    } else if (contactHistory.contactExecuteResultCode === CONTACT_RESULT_CODE.SLIP_ISSUE) {
      return this.getSlipKindKey(contactHistory).pipe(
        map(
          slipKindKey => {
            if (!_isNil(slipKindKey)) {
              const urlQuery = new BlUrlQuery();
              urlQuery.setValue(BlUrlQueryKey.ClearBsid, 1);
              this.transitionDeciderService.assignSlipQueryParam(urlQuery, slipKindKey);
              const path = slipKindKey.slipKey.productCode === ProductCode.SuperFrontman
                ? PagePath.SlipEntry : PagePath.RepairSlipEntryCondition;
              return UrlUtils.buildUrlPath(path, { params: urlQuery.getQueryParams([], true) });
            }
          }
        )
      );
    }

    return RxOf(null);
  }

  /**
   * 入力検証ルールを取得します。
   * @param isImplemented 実施済フラグ
   * @returns 入力検証ルール
   */
  private getCustomRules(isImplemented: boolean): Observable<Rules[]> {
    // コンタクト情報のルールを子エンティティ込みで丸ごと取得は重いので分割して取得
    return RxForkJoin(
      this.contactResource.getRules<Rules[]>(),
      this.contactHistoryResource.getRules<Rules[]>()
    ).pipe(
      map(
        ([contactRules, contactHistoryRules]) => {
          contactRules[0].propertySchemaDefines = _map(contactRules[0].propertySchemaDefines, define => {
            // 再コンタクト従業員名称は未実施なら必須
            if (define.propertyId === _last(_split(DEFINE.RULE_KEY.RE_EMPLOYEE_NAME, '#'))) {
              define.requiredInputDiv = !isImplemented;
            }

            // 再コンタクト実施予定日時は未実施なら必須
            if (define.propertyId === _last(_split(DEFINE.RULE_KEY.RE_SCHEDULE_DATE, '#'))) {
              define.requiredInputDiv = !isImplemented;
            }
            return define;
          });

          contactHistoryRules[0].propertySchemaDefines = _map(contactHistoryRules[0].propertySchemaDefines, define => {
            // コンタクト実施結果コードを必須に変更
            if (define.propertyId === _last(_split(DEFINE.RULE_KEY.EXECUTE_RESULT_CODE, '#'))) {
              define.requiredInputDiv = true;
              define.minValue = 1;
            }

            // コンタクトコメントは商談メモに合わせて最大文字数を200に変更 TODO nakagawa DDが修正されたら不要
            if (define.propertyId === _last(_split(DEFINE.RULE_KEY.COMMENT, '#'))) {
              define.maxLength = 200;
            }
            return define;
          });

          // マージして返却
          contactRules[0].propertySchemaDefines = _concat(
            contactRules[0].propertySchemaDefines,
            contactHistoryRules[0].propertySchemaDefines
          );
          return contactRules;
        }
      )
    );
  }

  /**
   * コンタクト実施結果リストを検索して返します。
   * @param organizationCode 組織コード
   * @returns コンタクト実施結果リスト
   */
  private searchContactExecuteResultList(organizationCode: string): Observable<IContactExecuteResult[]> {
    return this.contactService.searchContactExecuteResultList(organizationCode).pipe(
      map(
        contactExecuteResultList => {
          // この画面の選択肢として使用するのは「手動生成」のコンタクト実施結果のみ
          return _filter(contactExecuteResultList, contactExecuteResult => {
            return contactExecuteResult.contactCreateDiv === ContactCreateDiv.Manual;
          });
        }
      )
    );
  }

  /**
   * コンタクト関連情報を検索します。
   * @param contact 拡張コンタクト情報
   */
  private fatchRelatedContact(contact: IContactEx): void {
    const params = new BlApiQueryParams();
    const conditions: BlConditionInfo[] = [
      { key: 'customerCode', value: contact.gridData.customerCode, type: BlConditionType.Equal }
    ];

    let vehicleMgtNumber$: Observable<number>;
    if (contact.gridData.vehicleMgtNumber > 0) {
      conditions.push({ key: 'vehicleMgtNumber', value: contact.gridData.vehicleMgtNumber, type: BlConditionType.Equal });
      vehicleMgtNumber$ = RxOf(contact.gridData.vehicleMgtNumber);
    }
    params.setConditionInfo(conditions);

    // 表示は最大3件ずつでよいが、画面表示中コンタクトを非表示にする事を考慮して4件ずつ取っておく
    // NOTE: このサイズ指定は以下のサイズに影響する謎仕様
    // * CustomerContactDisp.contactInfoScheduleList
    // * CustomerContactDisp.contactInfoHistoryList
    params.setSize(4);

    // 画面表示中コンタクトの識別キーを取得
    const targetKey = this.getContactKey(contact);

    // 車両が確定していないなら車両管理番号を検索する
    if (_isNil(vehicleMgtNumber$)) {
      vehicleMgtNumber$ = this.searchVehicleMgtNumber(contact.gridData.customerCode);
    }
    RxForkJoin(
      // NOTE: 方式不正APIなので search だが結果は規定の型ではなく「CustomerContactDisp」がそのまま返ってくる
      this.customerContactDispResource.get<ICustomerContactDisp>({ params, entityOptions: { exPath: 'customercontactdisp' } }),
      vehicleMgtNumber$
    ).subscribe(
      ([customerContactDisp, targetVehicleMgtNumber]) => {
        // 予定リスト
        const scheduleList = _compact(_map(customerContactDisp.contactInfoScheduleList, entity => {
          // 画面表示中コンタクトは不要
          const key = this.getContactKey(entity);
          if (key === targetKey) {
            return null;
          }

          const scheduleHistory: IScheduleHistory = {
            key,
            isHistory: false,
            itemServiceColor: entity.itemServiceColor,
            dateTime: entity.taskInfo.contactScheduleDateTime,
            showTimeFlg: entity.taskInfo.contactScheduleTimeExistFlag,
            executeEmployeeName: entity.taskInfo.employeeName,
            executeItem: entity.taskInfo.executeOutline,
            contactComment: entity.taskInfo.contactComment
          };

          return scheduleHistory;
        }));

        // 最高3件まで
        if (scheduleList.length > 3) {
          scheduleList.length = 3;
        }

        // 結果リスト
        const historyList = _compact(_map(customerContactDisp.contactInfoHistoryList, entity => {
          // 画面表示中コンタクトは不要
          const key = this.getContactKey(entity);
          if (key === targetKey) {
            return null;
          }

          const scheduleHistory: IScheduleHistory = {
            key,
            isHistory: true,
            itemServiceColor: entity.itemServiceColor,
            dateTime: entity.contactHistory.contactDateTime,
            showTimeFlg: true,
            executeEmployeeName: entity.contactHistory.contactEmployeeName,
            executeItem: entity.contactHistory.contactItemDetail,
            executeResult: entity.contactHistory.contactExecuteResult,
            lostReason: entity.contactHistory.contactLostReason,
            contactComment: entity.contactHistory.contactComment
          };

          // 実施結果コードが「受付」or「伝票発行」の場合は遷移URLを生成するためにコンタクト履歴を持っておく
          if (entity.contactHistory.contactExecuteResultCode === CONTACT_RESULT_CODE.RECEPTION
            || entity.contactHistory.contactExecuteResultCode === CONTACT_RESULT_CODE.SLIP_ISSUE) {
            scheduleHistory.contactHistory = entity.contactHistory;
          }

          return scheduleHistory;

        }));

        // 最高3件まで
        if (historyList.length > 3) {
          historyList.length = 3;
        }

        // 予定と履歴をマージして日時の降順でソート
        const scheduleHistoryList = _sortBy(_concat(scheduleList, historyList), 'dateTime').reverse();

        // 通知
        this.relatedContactSubject.next({ tempId: contact.gridData.tempId, targetVehicleMgtNumber, scheduleHistoryList });
      },
      error => {
        this.relatedContactSubject.error(error);
      }
    );
  }

  /**
   * 指定顧客が保持している車両管理番号を検索します。
   *
   * ※車両なし顧客または複数車両を保持している顧客で車両管理番号を確定できない場合はnullを返します。
   * @param customerCode 顧客コード
   * @returns 車両管理番号
   */
  private searchVehicleMgtNumber(customerCode: number): Observable<number> {
    const conditions = [
      { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal },
      { key: 'customerCode', value: customerCode, type: BlConditionType.Equal }
    ];
    const fields = ['vehicleMgtNumber'];
    return this.vehicleInfoResource.getAll<IVehicleInfo>({ conditions, fields }).pipe(
      map(
        response => {
          let result: number = null;
          if (response.totalCount === 1) {
            // 確定できる場合のみ車両管理番号返却
            result = _head(response.searchResultList).vehicleMgtNumber;
          }
          return result;
        }
      )
    );
  }

  /**
   * コンタクト情報の識別キーを取得します。
   * @param contact コンタクト情報
   * @returns 識別キー
   */
  private getContactKey(contact: IContact): string {
    // コンタクト情報はPKを持っていない＆保持データも状態により異なるので独自生成
    let key = null;
    if (!_isNil(contact)) {
      if (!_isNil(contact.taskInfo)) {
        key = `T_${this.taskInfoResource.getIdString(contact.taskInfo)}`;

      } else if (!_isNil(contact.contactHistory)) {
        key = `H_${this.contactHistoryResource.getIdString(contact.contactHistory)}`;
      }
    }
    return key;
  }

  /**
   * コンタクト履歴から伝票種別キー管理を取得します。
   * @param contactHistory コンタクト履歴
   * @returns 伝票種別キー管理
   */
  private getSlipKindKey(contactHistory: IContactHistory): Observable<ISlipKindKey> {
    return this.contactHistoryOriginResource.post<IContactHistoryOrigin>(
      contactHistory,
      { entityOptions: { exPath: 'getcontacthistoryorigin' } }
    ).pipe(
      map(
        contactHistoryOrigin => {
          return !_isNil(contactHistoryOrigin) ? contactHistoryOrigin.slipKindKey : null;
        }
      )
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/page/call-list-page/result-form-modal/result-form-modal.service.ts